<template>
    <div
        class="high-level d-flex flex-wrap align-items-center justify-content-between px-4 sticky-header"
        ref="highLevelRef"
        :class="{ expanded: shouldHideInfo }"
    >
        <div class="meta-holder d-flex align-items-center pt-5 flex-wrap w-full">
            <div class="w-full d-flex align-items-center justify-content-between">
                <div class="left-details d-flex align-items-center">
                    <p
                        class="geist-font link"
                        @click="goToGooglePatents(patentDetails?.googlePatentsLink)"
                    >
                        {{ patentDetails?.patentId }}
                    </p>
                    <div class="divide"></div>
                    <p class="dungeonDepths--text">
                        {{ patentDetails?.applicationNumber }}
                    </p>
                    <div class="divide"></div>
                    <div class="status d-flex align-items-center">
                        <div
                            class="status-dot"
                            :class="patentDetails?.status === 'Active' ? 'success' : 'grey03'"
                        ></div>
                        <p class="dungeonDepths--text ml-2 mr-1 geist-font">
                            {{ patentDetails?.status === 'Active' ? 'Active' : 'Inactive' }}
                        </p>
                    </div>
                </div>
                <div
                    v-if="allowPruning"
                    class="d-flex pointer prune-icon justify-content-start align-items-center prune-button"
                    :class="{ disabled: loadingPatentDetail }"
                    @click="prunePatent"
                >
                    <p class="dungeonDepths--text mr-2">
                        {{ patentDetails?.prune ? 'Unprune' : 'Prune' }}
                    </p>
                    <svg
                        class="mt-1 mr-n1"
                        :class="`${patentDetails?.prune ? 'pruned' : 'not-pruned'}`"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 16 16"
                    >
                        <path
                            d="M5.33333 6.68333V12H6.66667V8.66667C7.37778 8.66667 8.05833 8.53333 8.70833 8.26667C9.35833 8 9.93333 7.61667 10.4333 7.11667C10.9333 6.61667 11.3194 6.03889 11.5917 5.38333C11.8639 4.72778 12 4.04444 12 3.33333V2H10.6667C9.95556 2 9.27222 2.13611 8.61667 2.40833C7.96111 2.68056 7.38333 3.06667 6.88333 3.56667C6.80556 3.64444 6.73333 3.71944 6.66667 3.79167C6.6 3.86389 6.53889 3.93889 6.48333 4.01667C6.36111 3.55 6.18611 3.11111 5.95833 2.7C5.73056 2.28889 5.44444 1.91111 5.1 1.56667C4.6 1.06667 4.02222 0.680556 3.36667 0.408333C2.71111 0.136111 2.03333 0 1.33333 0H0V1.33333C0 2.04444 0.133333 2.72778 0.4 3.38333C0.666667 4.03889 1.05 4.61667 1.55 5.11667C2.05 5.61667 2.62778 6.00278 3.28333 6.275C3.93889 6.54722 4.62222 6.68333 5.33333 6.68333Z"
                            fill="#000000"
                        />
                    </svg>
                </div>
            </div>
        </div>
        <div class="title mt-3 w-full" style="min-height: 40px">
            <p
                class="geist-font semi-bold knights-cloak--text large"
                :class="{ 'overflow-ellipsis': titleEllipsis }"
            >
                {{ patentDetails?.title }}
            </p>
        </div>
    </div>

    <!-- Sub-meta - Entities, Dates  -->
    <div class="info-container">
        <div
            class="assignee d-flex my-2 px-4"
            ref="assigneeRef"
            :class="{ 'hide-info': shouldHideInfo }"
        >
            <div class="w-full d-flex align-items-center">
                <font-awesome-icon
                    :icon="`fas fa-building`"
                    class="castle-stone-600--text mr-2 mt-n1"
                    v-tooltip="{
                        content: 'Current Assignee',
                        placement: 'left',
                        delay: { show: 500, hide: 0 },
                    }"
                />
                <p class="no-wrap overflow-ellipsis geist-font semi-bold castle-stone-600--text">
                    {{ patentDetails?.entities ? patentDetails?.entities : 'N/A' }}
                </p>
                <div class="divide"></div>
                <p class="no-wrap overflow-ellipsis geist-font semi-bold castle-stone-600--text">
                    <span>Ultimate Parent: </span
                    >{{ patentDetails?.ultimateEntities ? patentDetails?.ultimateEntities : 'N/A' }}
                </p>
            </div>
        </div>
        <div class="dates px-4" ref="datesRef" :class="{ 'hide-info': shouldHideInfo }">
            <div class="w-full d-flex align-items-center">
                <font-awesome-icon
                    :icon="`fas fa-calendar`"
                    class="castle-stone-600--text mr-2 mt-n1"
                    v-tooltip="{
                        content: 'Current Assignee',
                        placement: 'left',
                        delay: { show: 500, hide: 0 },
                    }"
                />
                <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                    <span>Filed </span>{{ filters.toUTCString(patentDetails?.filingDate) }}
                </p>
                <div class="divide"></div>
                <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                    <span>Publication: </span
                    >{{ filters.toUTCString(patentDetails?.publicationDate) }}
                </p>
                <div class="divide"></div>
                <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                    <span>Priority: </span>{{ filters.toUTCString(patentDetails?.priorityDate) }}
                </p>
            </div>
        </div>
    </div>
    <div class="info-spacer" :class="{ active: shouldHideInfo }"></div>
</template>

<script setup>
import { ref, inject } from 'vue'

const filters = inject('filters')

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    allowPruning: {
        type: Boolean,
        default: false,
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
    shouldHideInfo: {
        type: Boolean,
        default: false,
    },
    titleEllipsis: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['prune-patent'])
const highLevelRef = ref(null)

const goToGooglePatents = (googleLink) => {
    if (googleLink) {
        window.open(`${googleLink}`, '_blank')
    }
}

const prunePatent = async () => {
    if (!props.patentDetails) return
    emit('prune-patent')
}

// Expose the ref to parent component
defineExpose({
    highLevelRef,
})
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.high-level {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;

    &.expanded {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
}

.divide {
    border-radius: 50%;
    width: 5px;
    height: 5px;
    background-color: vars.$grey04;
    margin: 0 12px;
}

.prune-button {
    background: vars.$grey07;
    padding: 10px 16px;
    border-radius: 8px;

    transition: all 0.2s ease-in-out;

    &:hover {
        background: vars.$grey05;
    }
}

:deep(.not-pruned) path {
    fill: vars.$grey04 !important;
}

:deep(.pruned) path {
    fill: vars.$success !important;
}

.status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
}
</style>
